import React, { useState } from 'react';
import { DownloadSimple, CurrencyCircleDollar } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import {
  trackShareDownload,
  trackShareCreationShare,
} from '../../../../analytics';

import { LinkBox } from '../../../../components/custom';
import { EmailSendGroup } from '../../../../components/form';
import { Spinner, Button, Tooltip } from '../../../../components/general';
import { downloadVideo } from '../../../../components/video';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { Person, PeopleEmailState } from '../../../../types';
import { isEmpty, getThumbnail } from '../../../../utils';

interface Props {
  person: Person;
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
  afterEmailSend: () => void;
  isIncreased: boolean;
}

const ShareFaceBlock: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { person, emailState, setEmailState, afterEmailSend, isIncreased } =
    props;

  const [isDownloading, setIsDownloading] = useState(false);

  const { face, metrics, creations } = person;
  const creation = creations?.[0];

  // sample project data
  const { isSample } = useSampleProjectData(eventId);

  if (isEmpty(person) || isEmpty(emailState)) return null;

  const handleDownload = async () => {
    setIsDownloading(true);

    await downloadVideo(person.videoUrl ?? '', face.name);

    trackShareDownload('creation', isSample);

    setIsDownloading(false);
  };

  const handleAfterEmailSend = () => {
    afterEmailSend();

    trackShareCreationShare(
      (metrics?.emailedCount ?? 0) > 0 ? 'Re-share' : 'First time',
    );
  };

  return (
    <div
      className={classNames({
        'relative flex flex-col items-center gap-4 text-start': true,
        'w-full sm:w-[calc((100%_-_(2rem_*_2))_/_3)]': !isIncreased,
        'w-full sm:w-[calc((100%_-_2rem)_/_2)]': isIncreased,
      })}
    >
      <video
        id="video"
        className="w-full aspect-video bg-black rounded-md object-contain"
        src={person.videoUrl}
        poster={creation?.frameId ? getThumbnail(creation.frameId) : undefined}
        controls
      />

      <div className="w-full flex items-center gap-4">
        <img
          src={person.img}
          className="w-10 min-w-10 aspect-square rounded-full object-cover"
          alt={`${face.name} Icon`}
        />

        <div className="flex items-center gap-1">
          <h5>{face.name}</h5>

          {face.payment?.paid && (
            <Tooltip
              text={
                face.payment.type === 'donate'
                  ? `Attendee donated $${face.payment.amount}`
                  : 'Attendee paid to remove the watermark'
              }
            >
              <div className="mr-1 leading-none cursor-pointer hover:text-primary-900">
                <CurrencyCircleDollar weight="fill" size="1.5rem" />
              </div>
            </Tooltip>
          )}
        </div>

        <div className="flex ml-auto">
          <LinkBox type="creation" link={person.shareLink ?? ''} btnOnly />

          <Button
            variant="square"
            colour="transparent-black"
            className="text-[1.25rem]"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? <Spinner size="sm" /> : <DownloadSimple />}
          </Button>
        </div>
      </div>

      <div className="w-full flex flex-col gap-2">
        <EmailSendGroup
          components={{ emailInput: true, sendBtn: 'square' }}
          type="individual-creation"
          person={person}
          emailState={emailState}
          setEmailState={setEmailState}
          afterEmailSend={handleAfterEmailSend}
          fieldText="share"
          className="w-full max-w-full"
        />

        {!isSample && (
          <div className="flex justify-between gap-2 sm:mt-[-0.25rem] text-sm italic opacity-80">
            <div>Emailed: {metrics?.emailedCount ?? 0}</div>
            <div>Played: {metrics?.viewedCount ?? 0}</div>
            <div>Downloaded: {metrics?.downloadedCount ?? 0}</div>
            <div>Shared: {metrics?.sharedCount ?? 0}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareFaceBlock;
